import { parseISO } from "date-fns";
import { FC, useMemo } from "react";
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { SKUAnalytics, TagAnalytics } from "@/data/types";
import { formatByPeriod } from "@/lib/utils";

const chartConfig = {
  external_verifications: {
    label: "API Verifications",
  },
  custom_verifications: {
    label: "Custom Verifications",
  },
  program_verifications: {
    label: "Programming Verifications",
  },
} satisfies ChartConfig;

type Props = {
  data?: SKUAnalytics | TagAnalytics;
};

export const VerificationsChart: FC<Props> = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data) {
      return [];
    }

    return Object.keys(data.tag_program_verifications_data).map((time) => {
      const datetime =
        data.period === "hour"
          ? parseISO(time).toLocaleString()
          : parseISO(time).toLocaleDateString();

      return {
        time: datetime,
        external_verifications: data.external_tag_verifications_data[time],
        custom_verifications: data.tag_custom_verifications_data[time],
        program_verifications: data.tag_program_verifications_data[time],
      };
    });
  }, [data]);

  return (
    <Card className="flex-1">
      <CardHeader>
        <CardTitle>Verifications</CardTitle>
        <CardDescription>Tag verification types made over time</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[240px] w-full">
          <LineChart accessibilityLayer data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="time"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={(value) => formatByPeriod(value, data?.period)}
            />
            <YAxis axisLine={false} tickLine={false} />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) =>
                    formatByPeriod(value, data?.period)
                  }
                />
              }
            />
            <Line
              name="Program"
              dataKey="program_verifications"
              type="monotone"
              stroke="var(--color-violet-500)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              name="Custom"
              dataKey="custom_verifications"
              type="monotone"
              stroke="var(--color-emerald-500)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              name="API"
              dataKey="external_verifications"
              type="monotone"
              stroke="var(--color-sky-500)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Legend iconSize={8} iconType="square" verticalAlign="bottom" />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
